import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

interface Props {
  setShowSetting?: (v: boolean) => void;
  isTab?: boolean;
}

export default function AdminSetting({ setShowSetting, isTab }: Props) {
  const [login, setLogin] = useState('');
  const [newPassword, setNewPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [mailFrom, setMailFrom] = useState('');
  const [mailAuth, setMailAuth] = useState('');
  const [mailSll, setMailSll] = useState<boolean>(false);
  const [mailHost, setMailHost] = useState('');
  const [mailPort, setMailPort] = useState('');
  const [mailUser, setMailUser] = useState('');
  const [mailService, setMailService] = useState('');
  const [mailPassword, setMailPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const { data } = await axios.get('admin/admin');
        if (data) {
          setMailFrom(data.mail_from);
          setMailHost(data.mail_host);
          setMailAuth(data.mail_authMethod);
          setMailPort(data.mail_port);
          setMailService(data.mail_service);
          setMailSll(Boolean(data.mail_secure));
          setMailUser(data.mail_user);
          setMailPassword(data.mail_pass);
          setLogin(data.email);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchAdmin();
  }, []);

  const handleSaveMail = async () => {
    try {
      const dataToSend: any = {
        email: login,
        mail_from: mailFrom,
        mail_host: mailHost,
        mail_authMethod: mailAuth,
        mail_port: Number(mailPort),
        mail_secure: mailSll,
        mail_service: mailService,
        mail_user: mailUser,
        mail_pass: mailPassword,
      };

      if (newPassword) {
        if (!password || !passwordConfirm) {
          enqueueSnackbar('Fill both password fields', { variant: 'warning' });
          return;
        }
        if (password !== passwordConfirm) {
          enqueueSnackbar('Passwords do not match', { variant: 'error' });
          return;
        }
        dataToSend.password = password;
      }

      await axios.put(`admin/update-admin/${Number(1)}/`, dataToSend);
      enqueueSnackbar('Change success', { variant: 'success' });
      if (setShowSetting) setShowSetting(false);
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const form = (
    <div className="text-[var(--text-color)]" style={{ maxWidth: isTab ? 520 : undefined }}>
      {/* Account */}
      <div className="mb-[6px] text-[10px] font-bold uppercase tracking-wider text-[var(--text-color-calendar-week)]">{t('Information')}</div>
      <div className="bg-[var(--btn-user-form)] rounded-[8px] p-[14px] mb-[14px] border border-[var(--border-input)]">
        <div className="flex flex-col mb-[10px]">
          <label className="text-[10px] font-semibold mb-[3px]">Login:</label>
          <input
            className="timesheet-edit-input w-full"
            type="text"
            title="Login"
            placeholder="Login"
            value={login || ''}
            onChange={(e) => setLogin(e.target.value)}
          />
        </div>
        {!newPassword ? (
          <button
            type="button"
            className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none text-[13px] py-[5px] px-[15px] cursor-pointer rounded-[5px] hover:bg-[var(--btn-user-hover)]"
            onClick={() => setNewPassword(true)}>
            {t('ChangePassword')}
          </button>
        ) : (
          <div className="flex gap-[8px]">
            <input
              placeholder="New password"
              title="New password"
              type="password"
              className="timesheet-edit-input flex-1"
              onChange={e => setPassword(e.target.value)}
            />
            <input
              placeholder="Confirm password"
              title="Confirm password"
              type="password"
              className="timesheet-edit-input flex-1"
              onChange={e => setPasswordConfirm(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Mail */}
      <div className="mb-[6px] text-[10px] font-bold uppercase tracking-wider text-[var(--text-color-calendar-week)]">{t('Mail')}</div>
      <div className="bg-[var(--btn-user-form)] rounded-[8px] p-[14px] mb-[14px] border border-[var(--border-input)]">
        <div className={`grid gap-[10px] ${isTab ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail Host:</label>
            <input className="timesheet-edit-input w-full" type="text" title="Mail Host" placeholder="smtp.example.com" value={mailHost || ''} onChange={(e) => setMailHost(e.target.value)} />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail Port:</label>
            <input
              className="timesheet-edit-input w-full"
              type="text"
              title="Mail Port"
              placeholder="25"
              value={mailPort || ''}
              onChange={(e) => setMailPort(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail User:</label>
            <input className="timesheet-edit-input w-full" type="text" title="Mail User" placeholder="user@example.com" value={mailUser || ''} onChange={(e) => setMailUser(e.target.value)} />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail Password:</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={mailPassword || ''}
                title="Mail Password"
                placeholder="Password"
                className="timesheet-edit-input w-full pr-[30px]"
                onChange={e => setMailPassword(e.target.value)}
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                className="absolute top-1/2 -translate-y-1/2 right-[10px] text-[12px] opacity-50 cursor-pointer hover:opacity-70"
                onClick={() => { if (mailPassword !== '*****') setShowPassword(!showPassword); }}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail Service:</label>
            <select className="timesheet-edit-input w-full" value={mailService || ''} onChange={(e) => setMailService(e.target.value)}>
              <option value="" disabled>select service</option>
              <option value="gmail">gmail</option>
              <option value="other">other</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail Auth:</label>
            <select className="timesheet-edit-input w-full" value={mailAuth || ''} onChange={(e) => setMailAuth(e.target.value)}>
              <option value="" disabled>select type</option>
              <option value="PLAIN">PLAIN</option>
              <option value="LOGIN">LOGIN</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] font-semibold mb-[3px]">Mail From:</label>
            <input className="timesheet-edit-input w-full" type="text" title="Mail From" placeholder="noreply@example.com" value={mailFrom || ''} onChange={(e) => setMailFrom(e.target.value)} />
          </div>
          <div className="flex items-end">
            <FormControlLabel
              control={<Checkbox onChange={() => setMailSll(prev => !prev)} checked={mailSll} size="small" />}
              label="TLS"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-[8px]">
        <button type="button" className="btn-primary" onClick={handleSaveMail}>{t('Change')}</button>
        {setShowSetting && (
          <button type="button" className="dt-btn-ghost" onClick={() => setShowSetting(false)}>{t('Cancel')}</button>
        )}
      </div>
    </div>
  );

  if (isTab) {
    return <div className="p-[4px]">{form}</div>;
  }

  return (
    <Box sx={{ width: 360, height: '200%', background: 'var(--bg-form)' }} role="presentation">
      <div className="my-[5px] mx-[25px]">
        <h1 className="text-[20px] font-bold mb-[10px] text-[var(--text-color)]">{t('Setting')}</h1>
        {form}
      </div>
    </Box>
  );
}
